import React, { useEffect, useState } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Row,
    Label,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import * as url from "../../helpers/url_helper"
import { validateBeforeConfirm } from "helpers/validation_helper"

import {
    getMerchantAttributes,
    manageMerchantAttribute,
    addAttributeToForm,
    updateAttributeInForm,
    removeAttributeFromForm,
    clearAttributeForm,
} from "store/merchants/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"
import TextInput from "components/Common/TextInput"

export default function MerchantAttributeForm({
    page,
    match,
    history,
    activeTab,
    tabId,
    createdMerchantID,
}) {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingAttributeIndex, setEditingAttributeIndex] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentAttribute, setCurrentAttribute] = useState({
        attributeKey: "",
        attributeValue: "",
    })

    const dispatch = useDispatch()
    const detailID = match?.params?.id

    const merchantID = page === "add" ? createdMerchantID : detailID

    // Validation schema
    const validationSchema = Yup.object({
        attributeKey: Yup.string()
            .required("Attribute key is required")
            .max(255, "Attribute key cannot exceed 255 characters")
            .trim("Attribute key cannot contain leading or trailing spaces"),
        attributeValue: Yup.string()
            .required("Attribute value is required")
            .max(255, "Attribute value cannot exceed 255 characters")
            .trim("Attribute value cannot contain leading or trailing spaces"),
    })

    // Form validation using Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            attributeKey: currentAttribute.attributeKey,
            attributeValue: currentAttribute.attributeValue,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddAttributeToForm()
            }
        },
    })

    // Update form values when currentAttribute changes
    useEffect(() => {
        validation.setValues({
            attributeKey: currentAttribute.attributeKey,
            attributeValue: currentAttribute.attributeValue,
        })
    }, [currentAttribute])

    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const {
        merchantAttributes,
        attributeError,
        isLoadingAttributes,
        attributeFormData,
        attributeCreationSuccess,
    } = useSelector(state => ({
        merchantAttributes: state.merchants.merchantAttributes || [],
        attributeError: state.merchants.attributeError,
        isLoadingAttributes: state.merchants.isLoadingAttributes || false,
        attributeFormData: state.merchants.attributeFormData || [],
        attributeFormError: state.merchants.attributeFormError,
        attributeCreationSuccess: state.merchants.attributeCreationSuccess,
    }))

    // Fetch attributes only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (detailID && isTabActive) {
            dispatch(
                getMerchantAttributes({
                    headers: {
                        "input-parameters": { merchantID: detailID },
                    },
                })
            )
        }
    }, [dispatch, detailID, activeTab, tabId])

    useEffect(() => {
        dispatch(clearAttributeForm())
    }, [dispatch])

    const handleAddAttributeToForm = async () => {
        // Validate form before proceeding
        const isValid = await validateBeforeConfirm(validation)
        if (!isValid) {
            return
        }

        const newAttribute = {
            attributeKey: validation.values.attributeKey.trim(),
            attributeValue: validation.values.attributeValue.trim(),
        }

        if (editingAttributeIndex !== null) {
            dispatch(updateAttributeInForm(editingAttributeIndex, newAttribute))
            setEditingAttributeIndex(null)
        } else {
            dispatch(addAttributeToForm(newAttribute))
        }

        // Reset form
        setCurrentAttribute({ attributeKey: "", attributeValue: "" })
        validation.resetForm()
    }

    const handleEditAttribute = (attribute, index) => {
        setCurrentAttribute({
            attributeKey: attribute.attributeKey,
            attributeValue: attribute.attributeValue,
        })
        setEditingAttributeIndex(index)
    }

    const handleDeleteAttributeFromForm = (index) => {
        dispatch(removeAttributeFromForm(index))

        if (editingAttributeIndex === index) {
            setEditingAttributeIndex(null)
            setCurrentAttribute({ attributeKey: "", attributeValue: "" })
            validation.resetForm()
        }
    }

    const handleCancelEdit = () => {
        setEditingAttributeIndex(null)
        setCurrentAttribute({ attributeKey: "", attributeValue: "" })
        validation.resetForm()
    }

    const handleFinalSubmit = () => {
        setIsSubmitting(true)

        const transformedAttributes = attributeFormData.map(attr => ({
            key: attr.attributeKey,
            value: attr.attributeValue,
        }))

        const payload = {
            merchantID: merchantID,
            attributes: transformedAttributes,
        }

        dispatch(manageMerchantAttribute(payload, {}, history))
    }

    useEffect(() => {
        if (attributeCreationSuccess && isSubmitting) {
            setIsSubmitting(false)
            setIsEditMode(false)

            if (page !== "add") {
                setShowAddForm(false)
            }

            if (detailID) {
                dispatch(
                    getMerchantAttributes({
                        headers: {
                            "input-parameters": { merchantID: detailID },
                        },
                    })
                )
            }
        }
    }, [attributeCreationSuccess, isSubmitting, page, detailID, dispatch])

    useEffect(() => {
        if (isSubmitting && attributeError) {
            setIsSubmitting(false)
        }
    }, [isSubmitting, attributeError])

    const handleAddNewAttribute = () => {
        dispatch(clearAttributeForm())
        setCurrentAttribute({ attributeKey: "", attributeValue: "" })
        setEditingAttributeIndex(null)
        setIsEditMode(false)
        setShowAddForm(true)
        validation.resetForm()
    }

    const handleEditExistingAttributes = () => {
        // Clear form 
        dispatch(clearAttributeForm())

        // Pre-fill form with existing attributes
        merchantAttributes.forEach(attr => {
            dispatch(addAttributeToForm({
                attributeKey: attr.attributeKey,
                attributeValue: attr.attributeValue
            }))
        })

        setIsEditMode(true)
        setShowAddForm(true)
        setCurrentAttribute({ attributeKey: "", attributeValue: "" })
        setEditingAttributeIndex(null)
        validation.resetForm()
    }

    const handleCancelForm = () => {
        dispatch(clearAttributeForm())
        setCurrentAttribute({ attributeKey: "", attributeValue: "" })
        setEditingAttributeIndex(null)
        setIsEditMode(false)
        validation.resetForm()
        if (page !== "add") {
            setShowAddForm(false)
        }
    }

    const handleDeleteAttribute = (attribute) => {
        const remainingAttributes = merchantAttributes
            .filter(attr => attr.attributeKey !== attribute.attributeKey)
            .map(attr => ({
                key: attr.attributeKey,
                value: attr.attributeValue,
            }))

        const payload = {
            merchantID: merchantID,
            attributes: remainingAttributes,
        }
        dispatch(manageMerchantAttribute(payload, {}, history))
    }

    // Handle input changes
    const handleInputChange = (field, value) => {
        setCurrentAttribute(prev => ({
            ...prev,
            [field]: value
        }))
        validation.setFieldValue(field, value)
    }

    // Create a proper validation object for read-only fields
    const getValidationForReadOnly = () => ({
        errors: {},
        touched: {},
        values: {},
        setFieldValue: () => { },
        handleBlur: () => { },
        isValid: true,
    })

    const getHeaderButton = () => {
        if (page === "add" || showAddForm) {
            return null
        }

        if (merchantAttributes.length === 0) {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_MERCHANT_ATTRIBUTE}
                    actionType="post"
                >
                    <CButton.Success
                        icon="bx bx-plus"
                        text="Add Attributes"
                        onClick={handleAddNewAttribute}
                    />
                </AccessControlWrapper>
            )
        } else {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_MERCHANT_ATTRIBUTE}
                    actionType="post"
                >
                    <CButton.Primary
                        text="Edit Attribute"
                        onClick={handleEditExistingAttributes}
                    />
                </AccessControlWrapper>
            )
        }
    }

    return (
        <>
            {/* Error Display */}
            {attributeError && shouldDisplayError(attributeError) && (
                <Alert color="danger" className="mb-3">
                    {attributeError.message || "An error occurred"}
                </Alert>
            )}

            {/* Add/Edit Attribute Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {isEditMode ? "Edit Attributes" : (attributeFormData.length > 0 ? "Manage Attributes" : "Add Attributes")}
                        </h6>

                        <Form
                            className="needs-validation"
                            onSubmit={e => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            {attributeFormData.map((attr, index) => (
                                <Row key={index} className="align-items-end mb-2">
                                    <Col md="4">
                                        <TextInput
                                            name={`attributeKey_${index}`}
                                            label="Key"
                                            placeholder="Enter key"
                                            value={editingAttributeIndex === index ? currentAttribute.attributeKey : attr.attributeKey}
                                            readOnly={editingAttributeIndex !== index || isSubmitting}
                                            maxLength={255}
                                            required={editingAttributeIndex === index}
                                            onChange={(e) => {
                                                if (editingAttributeIndex === index) {
                                                    handleInputChange('attributeKey', e.target.value)
                                                }
                                            }}
                                            validation={editingAttributeIndex === index ? validation : getValidationForReadOnly()}
                                            inputClassName={editingAttributeIndex !== index ? "text-dark" : ""}
                                        />
                                    </Col>
                                    <Col md="4">
                                        <TextInput
                                            name={`attributeValue_${index}`}
                                            label="Value"
                                            placeholder="Enter value"
                                            value={editingAttributeIndex === index ? currentAttribute.attributeValue : attr.attributeValue}
                                            readOnly={editingAttributeIndex !== index || isSubmitting}
                                            maxLength={255}
                                            required={editingAttributeIndex === index}
                                            onChange={(e) => {
                                                if (editingAttributeIndex === index) {
                                                    handleInputChange('attributeValue', e.target.value)
                                                }
                                            }}
                                            validation={editingAttributeIndex === index ? validation : getValidationForReadOnly()}
                                            inputClassName={editingAttributeIndex !== index ? "text-dark" : ""}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mb-2">
                                            {editingAttributeIndex === index ? (
                                                <>
                                                    <IconButton.Save
                                                        onClick={handleAddAttributeToForm}
                                                        title="Update attribute"
                                                        disabled={isSubmitting || !validation.isValid || !currentAttribute.attributeKey.trim() || !currentAttribute.attributeValue.trim()}
                                                    />
                                                    <IconButton.Cancel
                                                        onClick={handleCancelEdit}
                                                        title="Cancel edit"
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleEditAttribute(attr, index)}
                                                        title="Edit attribute"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-edit"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteAttributeFromForm(index)}
                                                        title="Delete attribute"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ))}

                            {editingAttributeIndex === null && (
                                <Row className="align-items-center mb-3">
                                    <Col md="4">
                                        <TextInput
                                            name="attributeKey"
                                            label="Key"
                                            placeholder="Enter key"
                                            value={currentAttribute.attributeKey}
                                            readOnly={isSubmitting}
                                            maxLength={255}
                                            required={true}
                                            onChange={(e) => handleInputChange('attributeKey', e.target.value)}
                                            validation={validation}
                                        />
                                    </Col>
                                    <Col md="4">
                                        <TextInput
                                            name="attributeValue"
                                            label="Value"
                                            placeholder="Enter value"
                                            value={currentAttribute.attributeValue}
                                            readOnly={isSubmitting}
                                            maxLength={255}
                                            required={true}
                                            onChange={(e) => handleInputChange('attributeValue', e.target.value)}
                                            validation={validation}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mt-3">
                                            <IconButton.Add
                                                onClick={handleAddAttributeToForm}
                                                disabled={isSubmitting || !validation.isValid || !currentAttribute.attributeKey.trim() || !currentAttribute.attributeValue.trim()}
                                                title="Add attribute"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </Form>

                        {/* Form Action Buttons */}
                        <div className="text-end">
                            {page === "detail" && (
                                <CButton.Secondary
                                    text="Cancel"
                                    className="me-2"
                                    onClick={handleCancelForm}
                                    disabled={isSubmitting}
                                />
                            )}

                            {(attributeFormData.length > 0 || isEditMode) && (
                                <ConfirmButton
                                    contextUri={url.MANAGE_MERCHANT_ATTRIBUTE}
                                    actionType="post"
                                    validateBeforeConfirm={() =>
                                        Promise.resolve(isEditMode || attributeFormData.length > 0)
                                    }
                                    onConfirm={handleFinalSubmit}
                                    message={`Are you sure you want to ${isEditMode ? 'update' : 'save'} these attributes?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <CButton.Primary
                                            text={isEditMode ? "Update Attributes" : "Save Attributes"}
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                        />
                                    }
                                />
                            )}

                        </div>
                    </CardBody>
                </Card>
            )}

            {!isEditMode && (
                <SimpleTableContainer
                    title="Attributes"
                    data={merchantAttributes.map((attr, index) => ({
                        ...attr,
                        id: index,
                    }))}
                    columns={[
                        {
                            key: "attributeKey",
                            label: "Key",
                        },
                        {
                            key: "attributeValue",
                            label: "Value",
                        },
                    ]}
                    loading={isLoadingAttributes}
                    emptyMessage="No attributes added yet"
                    emptyIcon="bx bx-info-circle"
                    showActions={false}
                    headerActions={getHeaderButton()}
                />
            )}
        </>
    )
}